import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import StorageService from '../../../services/StorageService';
import { API_URLS } from '../../../constants/api-urls';
import { tradeCompanyProfileFromResponse } from '../../website/logisticsRfqBuyerPrefill';
import { useLocationMaster } from '../../../hooks/useLocationMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { getCurrentRole } from '../../../utils/roleSwitcher';
import '../../../assets/styles/eway_bill_generate.scss';

function parseAddressObject(raw) {
  if (raw == null) return { line: '', country: '', state: '', city: '', pincode: '' };
  let o = raw;
  if (typeof raw === 'string') {
    try {
      o = JSON.parse(raw);
    } catch {
      return { line: String(raw).trim(), country: '', state: '', city: '', pincode: '' };
    }
  }
  if (typeof o !== 'object' || o == null) return { line: '', country: '', state: '', city: '', pincode: '' };
  const line = String(
    o.address ?? o.line1 ?? o.street ?? o.streetAddress ?? o.street_address ?? ''
  ).trim();
  const extra = [o.line2, o.line3].filter(Boolean).join(', ').trim();
  return {
    line: line || extra,
    country: String(o.country ?? '').trim(),
    state: String(o.state ?? '').trim(),
    city: String(o.city ?? '').trim(),
    pincode: String(o.pincode ?? o.zip ?? o.postalCode ?? o.postal_code ?? '').trim(),
  };
}

function registrationBlock(certInput) {
  const c = certInput;
  if (!c || typeof c !== 'object') return {};
  return c.registrations || c.companyRegistrations || c.company_registrations || {};
}

/** `company_profile.tax_number` and optional `certifications.registrations.gstin`. */
function pickTaxIdFromCompanyProfile(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const regs = registrationBlock(profile.certifications);
  return String(
    regs.gstin ||
      regs.GSTIN ||
      profile.tax_number ||
      profile.taxNumber ||
      profile.gstin ||
      profile.GSTIN ||
      profile.company_gstin ||
      profile.companyGstin ||
      ''
  ).trim();
}

/** Public `GET /company/logistics-providers/:id` — `logistics_provider_profile` + `company_profile`. */
function pickGstinFromLogisticsProviderPublic(row) {
  if (!row || typeof row !== 'object') return '';
  const regs = registrationBlock(row.certifications);
  const bi =
    row.basic_information && typeof row.basic_information === 'object' ? row.basic_information : {};
  return String(
    row.tax_number ||
      row.taxNumber ||
      regs.gstin ||
      regs.GSTIN ||
      bi.gstin ||
      bi.GSTIN ||
      bi.companyGstin ||
      bi.company_gstin ||
      ''
  ).trim();
}

function providerDisplayName(row) {
  if (!row || typeof row !== 'object') return '';
  const bi =
    row.basic_information && typeof row.basic_information === 'object' ? row.basic_information : {};
  return String(row.company_name || bi.companyName || bi.company_name || '').trim();
}

export default function EWayBillGenerate() {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceId = location.state?.invoiceId;
  const logisticsQuoteId = location.state?.logisticsQuoteId;
  const logisticsSubmissionId = location.state?.logisticsSubmissionId;
  const logisticsReferenceNumber = location.state?.logisticsReferenceNumber;
  const ewayBillId = location.state?.ewayBillId;
  const { countries, states, cities, updateListsForCountry, updateListsForState } = useLocationMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();

  const [formData, setFormData] = useState({
    invoice_id: invoiceId || null,
    logistics_quote_id: logisticsQuoteId || null,
    logistics_generic_rfq_id: logisticsSubmissionId || null,
    document_type: 'Tax Invoice',
    document_number: '',
    document_date: new Date().toISOString().split('T')[0],
    from_name: '',
    from_gstin: '',
    from_address: '',
    from_country: 'India',
    from_state: '',
    from_city: '',
    from_pincode: '',
    to_name: '',
    to_gstin: '',
    to_address: '',
    to_country: '',
    to_state: '',
    to_city: '',
    to_pincode: '',
    supply_type: 'Outward (Supply)',
    sub_supply_type: 'Supply',
    transaction_type: 'Regular',
    transport_mode: 'Road',
    distance_km: '',
    transporter_name: '',
    transporter_gstin: '',
    transport_document_no: '',
    transport_document_date: '',
    vehicle_type: 'Regular',
    vehicle_type_description: '',
    vehicle_number: '',
    reason_for_transportation: 'Supply of goods',
    main_hsn_code: '',
    remarks: ''
  });

  const [validUpto, setValidUpto] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLineItems, setInvoiceLineItems] = useState([]);
  const [currentEWayBillNumber, setCurrentEWayBillNumber] = useState(null); // Store ewb_number for updates

  // Logistics flow uses manual consignee entry (no invoice preload)
  const isLogisticsFlow = Boolean(logisticsQuoteId) && !invoiceId;

  // Marketplace invoice e-way bills are seller-only.
  useEffect(() => {
    if (!invoiceId || isLogisticsFlow) return;
    if (Number(getCurrentRole()?.id) === 2) return;
    toast.error('Only the seller can generate E-Way Bills for marketplace invoices');
    navigate('/eway-bill-tracking', { replace: true });
  }, [invoiceId, isLogisticsFlow, navigate]);
  
  // Refs to track loaded countries/states to prevent infinite loops
  const loadedFromCountryRef = useRef(null);
  const loadedFromStateRef = useRef(null);
  const loadedToCountryRef = useRef(null);
  const loadedToStateRef = useRef(null);

  // Helper function to format date to YYYY-MM-DD
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // If it's a datetime string, extract just the date part
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      return dateValue.split('T')[0];
    }
    // If it's a datetime string with space separator (MySQL format)
    if (typeof dateValue === 'string' && dateValue.includes(' ')) {
      return dateValue.split(' ')[0];
    }
    // Try to parse as Date object
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      console.warn('Failed to parse date:', dateValue);
    }
    return '';
  };

  // Load E-Way Bill draft if ewayBillId is provided
  useEffect(() => {
    if (ewayBillId) {
      loadEWayBillDraft();
    } else if (invoiceId) {
      loadInvoiceData();
    } else if (logisticsQuoteId) {
      // Logistics post-pickup flow: keep UI the same, but allow manual entry.
      // We only prefill the document number from the logistics reference number if present.
      if (logisticsReferenceNumber) {
        setFormData((prev) => ({ ...prev, document_number: String(logisticsReferenceNumber) }));
      }
    }
  }, [ewayBillId, invoiceId, logisticsQuoteId, logisticsReferenceNumber]);

  // Logistics flow: auto-populate consignor/consignee from logistics RFQ payload + buyer company profile.
  useEffect(() => {
    if (!isLogisticsFlow) return;
    let cancelled = false;
    const strVal = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : '');

    (async () => {
      try {
        /** Consignor always uses logged-in user's `company_profile` + `user_login` (GET /company/my-profile). */
        let buyerProfile = null;
        try {
          const myRes = await CommonService.get('/company/my-profile');
          buyerProfile = tradeCompanyProfileFromResponse(myRes);
        } catch {
          buyerProfile = null;
        }

        const sessionUser = StorageService.getUser ? StorageService.getUser() : null;
        const regAddr = parseAddressObject(buyerProfile?.address);
        const buyerGstin = pickTaxIdFromCompanyProfile(buyerProfile);
        const buyerCompanyName =
          strVal(buyerProfile?.company_name) ||
          strVal(sessionUser?.company_name) ||
          strVal(sessionUser?.companyName);

        const applyBuyerConsignorOnly = async () => {
          const regCountryId = findCountryId(regAddr.country);
          if (regCountryId) await updateListsForCountry(regCountryId);
          const regStateId = findStateId(regAddr.state);
          if (regCountryId && regStateId) await updateListsForState(regCountryId, regStateId);
          const regCountryLabel = findCountryLabel(regAddr.country) || strVal(regAddr.country) || 'India';
          const regStateLabel = findStateLabel(regAddr.state) || strVal(regAddr.state) || '';
          const regCityLabel = findCityLabel(regAddr.city) || strVal(regAddr.city) || '';
          if (cancelled) return;
          setFormData((prev) => ({
            ...prev,
            from_name: prev.from_name || buyerCompanyName || strVal(sessionUser?.name),
            from_gstin: prev.from_gstin || buyerGstin,
            from_address: prev.from_address || regAddr.line,
            from_country: prev.from_country || regCountryLabel || 'India',
            from_state: prev.from_state || regStateLabel,
            from_city: prev.from_city || regCityLabel,
            from_pincode: prev.from_pincode || regAddr.pincode,
          }));
        };

        let submissionId = logisticsSubmissionId != null ? Number(logisticsSubmissionId) : NaN;
        if (!Number.isFinite(submissionId) || submissionId <= 0) {
          const qid = logisticsQuoteId != null ? Number(logisticsQuoteId) : NaN;
          if (Number.isFinite(qid) && qid > 0) {
            try {
              const listRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSIONS, {
                detail: 1,
              });
              const listBody = listRes?.data !== undefined ? listRes.data : listRes;
              const rowsRaw = listBody?.data !== undefined ? listBody.data : listBody;
              const rows = Array.isArray(rowsRaw) ? rowsRaw : [];
              const match = rows.find((r) => Number(r.acceptedQuoteId ?? r.accepted_quote_id ?? 0) === qid);
              const sid = match != null ? Number(match.id ?? match.submission_id) : NaN;
              if (Number.isFinite(sid) && sid > 0) submissionId = sid;
            } catch {
              /* ignore */
            }
          }
        }
        if (!Number.isFinite(submissionId) || submissionId <= 0) {
          await applyBuyerConsignorOnly();
          return;
        }

        const subRes = await CommonService.get(`/company/logistics-providers/rfq/submission/${submissionId}`);
        const pack = subRes?.data !== undefined ? subRes.data : subRes;
        const inner = pack?.data !== undefined ? pack.data : pack;
        if (!inner || typeof inner !== 'object') {
          await applyBuyerConsignorOnly();
          return;
        }

        const payload =
          inner.payload && typeof inner.payload === 'object' && !Array.isArray(inner.payload)
            ? inner.payload
            : inner.payload_json && typeof inner.payload_json === 'object' && !Array.isArray(inner.payload_json)
              ? inner.payload_json
              : {};
        const rfqKind = String(inner.rfqKind ?? inner.rfq_kind ?? payload.rfqKind ?? 'generic')
          .trim()
          .toLowerCase();

        const providerCompanyIdRaw =
          inner.logistics_provider_company_id ??
          inner.logisticsProviderCompanyId ??
          payload.companyId ??
          payload.logisticsProviderCompanyId;
        const providerCompanyId =
          providerCompanyIdRaw != null && Number.isFinite(Number(providerCompanyIdRaw))
            ? Number(providerCompanyIdRaw)
            : null;

        let providerPublic = null;
        if (providerCompanyId != null && providerCompanyId > 0) {
          try {
            const pr = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.PUBLIC_PROFILE(providerCompanyId));
            const prPack = pr?.data !== undefined ? pr.data : pr;
            providerPublic = prPack?.data !== undefined ? prPack.data : prPack;
            if (providerPublic && typeof providerPublic !== 'object') providerPublic = null;
          } catch {
            providerPublic = null;
          }
        }

        const originCountryId = findCountryId(payload.originCountry);
        if (originCountryId) await updateListsForCountry(originCountryId);
        const originStateId = findStateId(payload.originState);
        if (originCountryId && originStateId) await updateListsForState(originCountryId, originStateId);
        const originCountryLabel = findCountryLabel(payload.originCountry);
        const originStateLabel = findStateLabel(payload.originState);
        const originCityLabel = findCityLabel(payload.originCity);

        const regCountryId = findCountryId(regAddr.country);
        if (regCountryId) await updateListsForCountry(regCountryId);
        const regStateId = findStateId(regAddr.state);
        if (regCountryId && regStateId) await updateListsForState(regCountryId, regStateId);
        const regCountryLabel = findCountryLabel(regAddr.country) || strVal(regAddr.country) || 'India';
        const regStateLabel = findStateLabel(regAddr.state) || strVal(regAddr.state) || '';
        const regCityLabel = findCityLabel(regAddr.city) || strVal(regAddr.city) || '';

        let destCountryLabel = '';
        let destStateLabel = '';
        let destCityLabel = '';
        let toName = '';
        let toGstin = '';
        let toAddress = '';
        let toPin = '';

        const isExhibition =
          rfqKind === 'exhibition-logistics' ||
          rfqKind === 'exhibition_logistics' ||
          rfqKind === 'exhibitionlogistics';
        const isRelocation = rfqKind === 'business-relocation' || rfqKind === 'business_relocation';

        if (isExhibition) {
          const vc = findCountryId(payload.venueCountry);
          if (vc) await updateListsForCountry(vc);
          const vs = findStateId(payload.venueState);
          if (vc && vs) await updateListsForState(vc, vs);
          destCountryLabel = findCountryLabel(payload.venueCountry) || strVal(payload.venueCountry);
          destStateLabel = findStateLabel(payload.venueState) || strVal(payload.venueState);
          destCityLabel = findCityLabel(payload.venueCity) || strVal(payload.venueCity);
          toName =
            strVal(payload.destCompanyName) ||
            strVal(payload.destinationCompanyName) ||
            strVal(payload.destContactName) ||
            strVal(payload.venueContactName) ||
            strVal(payload.contactPerson) ||
            strVal(payload.companyName);
          toGstin =
            strVal(payload.destGstin) ||
            strVal(payload.destinationGstin) ||
            strVal(payload.destCompanyGstin) ||
            strVal(payload.dest_company_gstin) ||
            strVal(payload.consigneeGstin) ||
            strVal(payload.consignee_gstin);
          toAddress = strVal(payload.venueAddress);
          toPin = strVal(payload.venuePincode);
        } else if (isRelocation) {
          const dc = findCountryId(payload.destCountry);
          if (dc) await updateListsForCountry(dc);
          const ds = findStateId(payload.destState);
          if (dc && ds) await updateListsForState(dc, ds);
          destCountryLabel = findCountryLabel(payload.destCountry) || strVal(payload.destCountry);
          destStateLabel = findStateLabel(payload.destState) || strVal(payload.destState);
          destCityLabel = findCityLabel(payload.destCity) || strVal(payload.destCity);
          toName =
            strVal(payload.destCompanyName) ||
            strVal(payload.destinationCompanyName) ||
            strVal(payload.destContactName) ||
            strVal(payload.destinationContactName) ||
            strVal(payload.contactPerson) ||
            strVal(payload.companyName);
          toGstin =
            strVal(payload.destGstin) ||
            strVal(payload.destinationGstin) ||
            strVal(payload.destCompanyGstin) ||
            strVal(payload.dest_company_gstin) ||
            strVal(payload.consigneeGstin) ||
            strVal(payload.consignee_gstin);
          toAddress = strVal(payload.destAddress);
          toPin = strVal(payload.destPincode) || strVal(payload.destZip);
        } else {
          const dc = findCountryId(payload.destCountry);
          if (dc) await updateListsForCountry(dc);
          const ds = findStateId(payload.destState);
          if (dc && ds) await updateListsForState(dc, ds);
          destCountryLabel = findCountryLabel(payload.destCountry) || strVal(payload.destCountry);
          destStateLabel = findStateLabel(payload.destState) || strVal(payload.destState);
          destCityLabel = findCityLabel(payload.destCity) || strVal(payload.destCity);
          toName =
            strVal(payload.destCompanyName) ||
            strVal(payload.destinationCompanyName) ||
            strVal(payload.destContactName) ||
            strVal(payload.contactPerson) ||
            strVal(payload.companyName);
          toGstin =
            strVal(payload.destGstin) ||
            strVal(payload.destinationGstin) ||
            strVal(payload.destCompanyGstin) ||
            strVal(payload.dest_company_gstin) ||
            strVal(payload.consigneeGstin) ||
            strVal(payload.consignee_gstin);
          toAddress = strVal(payload.destAddress);
          toPin = strVal(payload.destZip) || strVal(payload.destPincode);
        }

        const providerGstin = pickGstinFromLogisticsProviderPublic(providerPublic);
        const providerName = providerDisplayName(providerPublic);
        if (!toGstin) toGstin = providerGstin;
        if (!toName) toName = providerName;

        const provAddr = parseAddressObject(providerPublic?.address);
        if (!toAddress && provAddr.line) {
          const pc = findCountryId(provAddr.country);
          if (pc) await updateListsForCountry(pc);
          const ps = findStateId(provAddr.state);
          if (pc && ps) await updateListsForState(pc, ps);
          toAddress = provAddr.line;
          if (!toPin) toPin = provAddr.pincode;
          if (!destCountryLabel) destCountryLabel = findCountryLabel(provAddr.country) || strVal(provAddr.country);
          if (!destStateLabel) destStateLabel = findStateLabel(provAddr.state) || strVal(provAddr.state);
          if (!destCityLabel) destCityLabel = findCityLabel(provAddr.city) || strVal(provAddr.city);
        }

        const fromAddressLine = strVal(payload.originAddress) || regAddr.line;
        const fromPin =
          strVal(payload.originZip) ||
          strVal(payload.originPincode) ||
          regAddr.pincode ||
          strVal(payload.originPostalCode);

        if (cancelled) return;
        setFormData((prev) => ({
          ...prev,
          logistics_generic_rfq_id: prev.logistics_generic_rfq_id || submissionId,
          from_name:
            prev.from_name ||
            buyerCompanyName ||
            strVal(payload.buyerCompanyName) ||
            strVal(payload.submitterCompanyName) ||
            strVal(payload.originContactName) ||
            '',
          from_gstin: prev.from_gstin || buyerGstin,
          from_address: prev.from_address || fromAddressLine,
          from_country: prev.from_country || originCountryLabel || regCountryLabel || 'India',
          from_state: prev.from_state || originStateLabel || regStateLabel || '',
          from_city: prev.from_city || originCityLabel || regCityLabel || '',
          from_pincode: prev.from_pincode || fromPin,

          to_name: prev.to_name || toName,
          to_gstin: prev.to_gstin || toGstin,
          to_address: prev.to_address || toAddress,
          to_country: prev.to_country || destCountryLabel,
          to_state: prev.to_state || destStateLabel,
          to_city: prev.to_city || destCityLabel,
          to_pincode: prev.to_pincode || toPin,
        }));
      } catch {
        /* keep manual entry */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogisticsFlow, logisticsSubmissionId, logisticsQuoteId]);

  // Load E-Way Bill draft data
  const loadEWayBillDraft = async () => {
    try {
      const response = await CommonService.get(`/eway-bill/${ewayBillId}`);
      if (response && response.success && response.data) {
        const ewayBill = response.data;
        
        // Load invoice data first
        if (ewayBill.invoice_id) {
          await loadInvoiceDataForId(ewayBill.invoice_id);
        }
        
        // Store ewb_number for updates
        if (ewayBill.ewb_number) {
          setCurrentEWayBillNumber(ewayBill.ewb_number);
        }
        
        // Populate form with E-Way Bill data
        setFormData(prev => ({
          ...prev,
          invoice_id: ewayBill.invoice_id || prev.invoice_id,
          document_type: ewayBill.document_type || prev.document_type,
          document_number: ewayBill.document_number || prev.document_number,
          document_date: formatDateForInput(ewayBill.document_date) || prev.document_date,
          from_name: ewayBill.from_name || prev.from_name,
          from_gstin: ewayBill.from_gstin || prev.from_gstin,
          from_address: ewayBill.from_address || prev.from_address,
          from_country: ewayBill.from_country || prev.from_country,
          from_state: ewayBill.from_state || prev.from_state,
          from_city: ewayBill.from_city || prev.from_city,
          from_pincode: ewayBill.from_pincode || prev.from_pincode,
          to_name: ewayBill.to_name || prev.to_name,
          to_gstin: ewayBill.to_gstin || prev.to_gstin,
          to_address: ewayBill.to_address || prev.to_address,
          to_country: ewayBill.to_country || prev.to_country,
          to_state: ewayBill.to_state || prev.to_state,
          to_city: ewayBill.to_city || prev.to_city,
          to_pincode: ewayBill.to_pincode || prev.to_pincode,
          supply_type: ewayBill.supply_type || prev.supply_type,
          sub_supply_type: ewayBill.sub_supply_type || prev.sub_supply_type,
          transaction_type: ewayBill.transaction_type || prev.transaction_type,
          transport_mode: ewayBill.transport_mode || prev.transport_mode,
          distance_km: ewayBill.distance_km ? String(ewayBill.distance_km) : prev.distance_km,
          transporter_name: ewayBill.transporter_name || prev.transporter_name,
          transporter_gstin: ewayBill.transporter_gstin || prev.transporter_gstin,
          transport_document_no: ewayBill.transport_document_no || prev.transport_document_no,
          transport_document_date: formatDateForInput(ewayBill.transport_document_date) || prev.transport_document_date,
          vehicle_type: ewayBill.vehicle_type || prev.vehicle_type,
          vehicle_type_description: ewayBill.vehicle_type_description || prev.vehicle_type_description,
          vehicle_number: ewayBill.vehicle_number || prev.vehicle_number,
          reason_for_transportation: ewayBill.reason_for_transportation || prev.reason_for_transportation,
          main_hsn_code: ewayBill.main_hsn_code || prev.main_hsn_code,
          remarks: ewayBill.remarks || prev.remarks
        }));
        
        if (ewayBill.valid_upto) {
          setValidUpto(formatDateForInput(ewayBill.valid_upto));
        }
      }
    } catch (error) {
      console.error('Error loading E-Way Bill draft:', error);
      toast.error('Failed to load E-Way Bill draft');
    }
  };

  // Load invoice data when invoice_id changes
  useEffect(() => {
    if (formData.invoice_id && formData.invoice_id !== invoiceId) {
      loadInvoiceData();
    }
  }, [formData.invoice_id]);

  const loadInvoiceData = async () => {
    try {
      const idToLoad = formData.invoice_id || invoiceId;
      if (!idToLoad) return;

      const response = await CommonService.get(`/seller-invoice/${idToLoad}`);
      if (response && response.success && response.data) {
        const invoice = response.data;
        setInvoiceData(invoice);
        setInvoiceLineItems(invoice.line_items || []);
        
        // Calculate main HSN code from line items
        const mainHsn = invoice.line_items && invoice.line_items.length > 0
          ? invoice.line_items[0].hsn_sac || ''
          : '';

        // Fetch company profiles for seller and buyer
        let sellerCompanyProfile = null;
        let buyerCompanyProfile = null;
        
        // Fetch seller company profile
        if (invoice.seller_company_id) {
          try {
            const sellerProfileResponse = await CommonService.get(`/company/profile/${invoice.seller_company_id}`);
            if (sellerProfileResponse && sellerProfileResponse.success && sellerProfileResponse.data) {
              sellerCompanyProfile = sellerProfileResponse.data;
            }
          } catch (error) {
            console.warn('Failed to fetch seller company profile:', error);
          }
        }
        
        // Fetch buyer company profile
        if (invoice.buyer_company_id) {
          try {
            const buyerProfileResponse = await CommonService.get(`/company/profile/${invoice.buyer_company_id}`);
            if (buyerProfileResponse && buyerProfileResponse.success && buyerProfileResponse.data) {
              buyerCompanyProfile = buyerProfileResponse.data;
            }
          } catch (error) {
            console.warn('Failed to fetch buyer company profile:', error);
          }
        }

        // Extract seller address from company profile
        let sellerAddress = '';
        let sellerCountry = 'India';
        let sellerState = '';
        let sellerCity = '';
        let sellerPincode = '';
        
        if (sellerCompanyProfile && sellerCompanyProfile.address) {
          // Handle address as object or JSON string
          let address = sellerCompanyProfile.address;
          if (typeof address === 'string') {
            try {
              address = JSON.parse(address);
            } catch (e) {
              console.warn('Failed to parse seller address JSON:', e);
            }
          }
          sellerAddress = address?.address || '';
          sellerCountry = address?.country || 'India';
          sellerState = address?.state || '';
          sellerCity = address?.city || '';
          sellerPincode = address?.pincode || '';
        } else {
          // Fallback to invoice data
          sellerAddress = Array.isArray(invoice.seller_address) 
            ? invoice.seller_address.join(', ') 
            : (invoice.seller_address || '');
          sellerState = invoice.shipping_state || '';
          sellerCity = invoice.shipping_city || '';
          sellerPincode = invoice.shipping_pincode || '';
        }

        // Extract buyer address from company profile
        let buyerAddress = '';
        let buyerCountry = '';
        let buyerState = '';
        let buyerCity = '';
        let buyerPincode = '';
        
        if (buyerCompanyProfile && buyerCompanyProfile.address) {
          // Handle address as object or JSON string
          let address = buyerCompanyProfile.address;
          if (typeof address === 'string') {
            try {
              address = JSON.parse(address);
            } catch (e) {
              console.warn('Failed to parse buyer address JSON:', e);
            }
          }
          buyerAddress = address?.address || '';
          buyerCountry = address?.country || '';
          buyerState = address?.state || '';
          buyerCity = address?.city || '';
          buyerPincode = address?.pincode || '';
        } else {
          // Fallback to invoice data
          buyerAddress = Array.isArray(invoice.buyer_address) 
            ? invoice.buyer_address.join(', ') 
            : (invoice.buyer_address || '');
          buyerCountry = invoice.shipping_country || '';
          buyerState = invoice.shipping_state || '';
          buyerCity = invoice.shipping_city || '';
          buyerPincode = invoice.shipping_pincode || '';
        }

        // Find country IDs and labels from master data
        const sellerCountryId = findCountryId(sellerCountry);
        const sellerCountryLabel = findCountryLabel(sellerCountry);
        const buyerCountryId = findCountryId(buyerCountry);
        const buyerCountryLabel = findCountryLabel(buyerCountry);
        
        // Use raw state/city values from company profile directly (they're likely already labels)
        // Set form data immediately with these values
        setFormData(prev => ({
          ...prev,
          document_number: invoice.invoice_number || '',
          document_date: formatDateForInput(invoice.invoice_date) || prev.document_date,
          from_name: sellerCompanyProfile?.company_name || invoice.seller_company_name || '',
          from_gstin: sellerCompanyProfile?.tax_number || invoice.seller_gstin || '',
          from_address: sellerAddress,
          from_country: sellerCountryLabel || 'India',
          from_state: sellerState || '',
          from_city: sellerCity || '',
          from_pincode: sellerPincode,
          to_name: buyerCompanyProfile?.company_name || invoice.buyer_company_name || '',
          to_gstin: buyerCompanyProfile?.tax_number || invoice.buyer_tax_id || '',
          to_address: buyerAddress,
          to_country: buyerCountryLabel || buyerCountry,
          to_state: buyerState || '',
          to_city: buyerCity || '',
          to_pincode: buyerPincode,
          reason_for_transportation: 'Supply of goods',
          main_hsn_code: mainHsn
        }));
        
        // Load location master data in the background for dropdowns
        // This will populate the dropdowns but won't block the form data setting
        if (sellerCountryId && !isNaN(Number(sellerCountryId))) {
          updateListsForCountry(Number(sellerCountryId)).then(() => {
            // After states load, try to load cities if we have a state
            if (sellerState) {
              const sellerStateId = findStateId(sellerState);
              if (sellerStateId && !isNaN(Number(sellerStateId))) {
                updateListsForState(Number(sellerCountryId), Number(sellerStateId));
              }
            }
          });
        }
        
        if (buyerCountryId && !isNaN(Number(buyerCountryId))) {
          updateListsForCountry(Number(buyerCountryId)).then(() => {
            // After states load, try to load cities if we have a state
            if (buyerState) {
              const buyerStateId = findStateId(buyerState);
              if (buyerStateId && !isNaN(Number(buyerStateId))) {
                updateListsForState(Number(buyerCountryId), Number(buyerStateId));
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toast.error('Failed to load invoice data');
    }
  };

  // Calculate valid upto date based on distance (200 KM/day rule)
  useEffect(() => {
    if (formData.distance_km && formData.document_date) {
      const distance = parseFloat(formData.distance_km);
      if (!isNaN(distance) && distance > 0) {
        const days = Math.ceil(distance / 200);
        const date = new Date(formData.document_date);
        date.setDate(date.getDate() + days);
        setValidUpto(date.toISOString().split('T')[0]);
      }
    }
  }, [formData.distance_km, formData.document_date]);

  // Load states when from_country changes
  useEffect(() => {
    if (formData.from_country && countries && countries.length > 0) {
      const country = countries.find(c => c.label === formData.from_country);
      const countryId = country?.id || country?.value;
      if (countryId && loadedFromCountryRef.current !== countryId) {
        loadedFromCountryRef.current = countryId;
        updateListsForCountry(countryId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.from_country]);

  // Load cities when from_state changes
  useEffect(() => {
    if (formData.from_state && formData.from_country) {
      // Wait a bit for states to load if they're not ready yet
      const checkAndLoad = () => {
        if (countries && countries.length > 0 && states && states.length > 0) {
          const country = countries.find(c => c.label === formData.from_country);
          const state = states.find(s => s.label === formData.from_state);
          const countryId = country?.id || country?.value;
          const stateId = state?.id || state?.value;
          const stateKey = `${countryId}-${stateId}`;
          if (countryId && stateId && loadedFromStateRef.current !== stateKey) {
            loadedFromStateRef.current = stateKey;
            updateListsForState(countryId, stateId);
          }
        }
      };
      
      // Try immediately
      checkAndLoad();
      // If states not loaded yet, try again after a short delay
      if (!states || states.length === 0) {
        const timer = setTimeout(checkAndLoad, 500);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.from_state, formData.from_country, states]);

  // Load states when to_country changes
  useEffect(() => {
    if (formData.to_country && countries && countries.length > 0) {
      const country = countries.find(c => c.label === formData.to_country);
      const countryId = country?.id || country?.value;
      if (countryId && loadedToCountryRef.current !== countryId) {
        loadedToCountryRef.current = countryId;
        updateListsForCountry(countryId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.to_country]);

  // Load cities when to_state changes
  useEffect(() => {
    if (formData.to_state && formData.to_country) {
      // Wait a bit for states to load if they're not ready yet
      const checkAndLoad = () => {
        if (countries && countries.length > 0 && states && states.length > 0) {
          const country = countries.find(c => c.label === formData.to_country);
          const state = states.find(s => s.label === formData.to_state);
          const countryId = country?.id || country?.value;
          const stateId = state?.id || state?.value;
          const stateKey = `${countryId}-${stateId}`;
          if (countryId && stateId && loadedToStateRef.current !== stateKey) {
            loadedToStateRef.current = stateKey;
            updateListsForState(countryId, stateId);
          }
        }
      };
      
      // Try immediately
      checkAndLoad();
      // If states not loaded yet, try again after a short delay
      if (!states || states.length === 0) {
        const timer = setTimeout(checkAndLoad, 500);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.to_state, formData.to_country, states]);

  const handleInputChange = async (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Reload invoice data if invoice_id changes
    if (field === 'invoice_id' && value) {
      await loadInvoiceDataForId(value);
    }
  };

  const loadInvoiceDataForId = async (id) => {
    try {
      const response = await CommonService.get(`/seller-invoice/${id}`);
      if (response && response.success && response.data) {
        const invoice = response.data;
        setInvoiceData(invoice);
        setInvoiceLineItems(invoice.line_items || []);
        
        const mainHsn = invoice.line_items && invoice.line_items.length > 0
          ? invoice.line_items[0].hsn_sac || ''
          : '';

        // Fetch company profiles for seller and buyer
        let sellerCompanyProfile = null;
        let buyerCompanyProfile = null;
        
        // Fetch seller company profile
        if (invoice.seller_company_id) {
          try {
            const sellerProfileResponse = await CommonService.get(`/company/profile/${invoice.seller_company_id}`);
            if (sellerProfileResponse && sellerProfileResponse.success && sellerProfileResponse.data) {
              sellerCompanyProfile = sellerProfileResponse.data;
            }
          } catch (error) {
            console.warn('Failed to fetch seller company profile:', error);
          }
        }
        
        // Fetch buyer company profile
        if (invoice.buyer_company_id) {
          try {
            const buyerProfileResponse = await CommonService.get(`/company/profile/${invoice.buyer_company_id}`);
            if (buyerProfileResponse && buyerProfileResponse.success && buyerProfileResponse.data) {
              buyerCompanyProfile = buyerProfileResponse.data;
            }
          } catch (error) {
            console.warn('Failed to fetch buyer company profile:', error);
          }
        }

        // Extract seller address from company profile
        let sellerAddress = '';
        let sellerCountry = 'India';
        let sellerState = '';
        let sellerCity = '';
        let sellerPincode = '';
        
        if (sellerCompanyProfile && sellerCompanyProfile.address) {
          // Handle address as object or JSON string
          let address = sellerCompanyProfile.address;
          if (typeof address === 'string') {
            try {
              address = JSON.parse(address);
            } catch (e) {
              console.warn('Failed to parse seller address JSON:', e);
            }
          }
          sellerAddress = address?.address || '';
          sellerCountry = address?.country || 'India';
          sellerState = address?.state || '';
          sellerCity = address?.city || '';
          sellerPincode = address?.pincode || '';
        } else {
          // Fallback to invoice data
          sellerAddress = Array.isArray(invoice.seller_address) 
            ? invoice.seller_address.join(', ') 
            : (invoice.seller_address || '');
          sellerState = invoice.shipping_state || '';
          sellerCity = invoice.shipping_city || '';
          sellerPincode = invoice.shipping_pincode || '';
        }

        // Extract buyer address from company profile
        let buyerAddress = '';
        let buyerCountry = '';
        let buyerState = '';
        let buyerCity = '';
        let buyerPincode = '';
        
        if (buyerCompanyProfile && buyerCompanyProfile.address) {
          // Handle address as object or JSON string
          let address = buyerCompanyProfile.address;
          if (typeof address === 'string') {
            try {
              address = JSON.parse(address);
            } catch (e) {
              console.warn('Failed to parse buyer address JSON:', e);
            }
          }
          buyerAddress = address?.address || '';
          buyerCountry = address?.country || '';
          buyerState = address?.state || '';
          buyerCity = address?.city || '';
          buyerPincode = address?.pincode || '';
        } else {
          // Fallback to invoice data
          buyerAddress = Array.isArray(invoice.buyer_address) 
            ? invoice.buyer_address.join(', ') 
            : (invoice.buyer_address || '');
          buyerCountry = invoice.shipping_country || '';
          buyerState = invoice.shipping_state || '';
          buyerCity = invoice.shipping_city || '';
          buyerPincode = invoice.shipping_pincode || '';
        }

        // Find country IDs and labels from master data
        const sellerCountryId = findCountryId(sellerCountry);
        const sellerCountryLabel = findCountryLabel(sellerCountry);
        const buyerCountryId = findCountryId(buyerCountry);
        const buyerCountryLabel = findCountryLabel(buyerCountry);
        
        // Use raw state/city values from company profile directly (they're likely already labels)
        // Set form data immediately with these values
        setFormData(prev => ({
          ...prev,
          document_number: invoice.invoice_number || '',
          document_date: formatDateForInput(invoice.invoice_date) || prev.document_date,
          from_name: sellerCompanyProfile?.company_name || invoice.seller_company_name || '',
          from_gstin: sellerCompanyProfile?.tax_number || invoice.seller_gstin || '',
          from_address: sellerAddress,
          from_country: sellerCountryLabel || 'India',
          from_state: sellerState || '',
          from_city: sellerCity || '',
          from_pincode: sellerPincode,
          to_name: buyerCompanyProfile?.company_name || invoice.buyer_company_name || '',
          to_gstin: buyerCompanyProfile?.tax_number || invoice.buyer_tax_id || '',
          to_address: buyerAddress,
          to_country: buyerCountryLabel || buyerCountry,
          to_state: buyerState || '',
          to_city: buyerCity || '',
          to_pincode: buyerPincode,
          reason_for_transportation: 'Supply of goods',
          main_hsn_code: mainHsn
        }));
        
        // Load location master data in the background for dropdowns
        // This will populate the dropdowns but won't block the form data setting
        if (sellerCountryId && !isNaN(Number(sellerCountryId))) {
          updateListsForCountry(Number(sellerCountryId)).then(() => {
            // After states load, try to load cities if we have a state
            if (sellerState) {
              const sellerStateId = findStateId(sellerState);
              if (sellerStateId && !isNaN(Number(sellerStateId))) {
                updateListsForState(Number(sellerCountryId), Number(sellerStateId));
              }
            }
          });
        }
        
        if (buyerCountryId && !isNaN(Number(buyerCountryId))) {
          updateListsForCountry(Number(buyerCountryId)).then(() => {
            // After states load, try to load cities if we have a state
            if (buyerState) {
              const buyerStateId = findStateId(buyerState);
              if (buyerStateId && !isNaN(Number(buyerStateId))) {
                updateListsForState(Number(buyerCountryId), Number(buyerStateId));
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toast.error('Failed to load invoice data');
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '₹0.00';
    return `₹${numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return '';
    }
  };

  // Calculate total invoice value
  const calculateTotalInvoiceValue = () => {
    if (!invoiceData) return 0;
    return invoiceData.grand_total || 0;
  };

  // Helper function to get unit label
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Helper function to find country ID from location master (for API calls)
  const findCountryId = (countryFromData) => {
    if (!countryFromData) return null;
    
    if (countries && countries.length > 0) {
      const foundCountry = countries.find(opt => 
        (opt.id && String(opt.id) === String(countryFromData)) ||
        (opt.value && String(opt.value) === String(countryFromData)) ||
        (opt.label && String(opt.label).toUpperCase() === String(countryFromData).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(countryFromData).toUpperCase())
      );
      if (foundCountry) {
        const countryId = foundCountry.id || foundCountry.value;
        // Only return if it's a numeric ID, not a label
        return countryId && !isNaN(Number(countryId)) ? Number(countryId) : null;
      }
    }
    
    // If we can't find a match, return null instead of the input
    // This prevents passing labels to API endpoints that expect IDs
    return null;
  };

  // Helper function to find country label from location master (for form display)
  const findCountryLabel = (countryFromData) => {
    if (!countryFromData) return '';
    
    if (countries && countries.length > 0) {
      const foundCountry = countries.find(opt => 
        (opt.id && String(opt.id) === String(countryFromData)) ||
        (opt.value && String(opt.value) === String(countryFromData)) ||
        (opt.label && String(opt.label).toUpperCase() === String(countryFromData).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(countryFromData).toUpperCase())
      );
      if (foundCountry) {
        return foundCountry.label || countryFromData;
      }
    }
    
    return countryFromData;
  };

  // Helper function to find state ID from location master (for API calls)
  const findStateId = (stateFromData) => {
    if (!stateFromData) return null;
    
    if (states && states.length > 0) {
      const foundState = states.find(opt => 
        (opt.id && String(opt.id) === String(stateFromData)) ||
        (opt.value && String(opt.value) === String(stateFromData)) ||
        (opt.label && String(opt.label).toUpperCase() === String(stateFromData).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(stateFromData).toUpperCase())
      );
      if (foundState) {
        const stateId = foundState.id || foundState.value;
        // Only return if it's a numeric ID, not a label
        return stateId && !isNaN(Number(stateId)) ? Number(stateId) : null;
      }
    }
    
    // If we can't find a match, return null instead of the input
    // This prevents passing labels to API endpoints that expect IDs
    return null;
  };

  // Helper function to find state label from location master (for form display)
  const findStateLabel = (stateFromData) => {
    if (!stateFromData) return '';
    
    if (states && states.length > 0) {
      const foundState = states.find(opt => 
        (opt.id && String(opt.id) === String(stateFromData)) ||
        (opt.value && String(opt.value) === String(stateFromData)) ||
        (opt.label && String(opt.label).toUpperCase() === String(stateFromData).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(stateFromData).toUpperCase())
      );
      if (foundState) {
        return foundState.label || stateFromData;
      }
    }
    
    return stateFromData;
  };

  // Helper function to find city label from location master (for form display)
  const findCityLabel = (cityFromData) => {
    if (!cityFromData) return '';
    
    if (cities && cities.length > 0) {
      const foundCity = cities.find(opt => 
        (opt.id && String(opt.id) === String(cityFromData)) ||
        (opt.value && String(opt.value) === String(cityFromData)) ||
        (opt.label && String(opt.label).toUpperCase() === String(cityFromData).toUpperCase()) ||
        (opt.name && String(opt.name).toUpperCase() === String(cityFromData).toUpperCase())
      );
      if (foundCity) {
        return foundCity.label || cityFromData;
      }
    }
    
    return cityFromData;
  };

  // Validate form data for E-Way Bill generation
  const validateForm = (isDraft = false) => {
    const errors = [];
    
    // Basic validations (required for both draft and generate)
    if (!isLogisticsFlow && !formData.invoice_id) {
      errors.push('Invoice ID is required');
    }
    if (isLogisticsFlow && !formData.logistics_quote_id) {
      errors.push('Logistics Quote ID is missing');
    }
    if (!formData.document_number) {
      errors.push('Document Number is required');
    }
    if (!formData.document_date) {
      errors.push('Document Date is required');
    }
    if (!formData.from_name) {
      errors.push('Supplier/Consignor Name is required');
    }
    if (!formData.from_gstin) {
      errors.push('Supplier/Consignor GSTIN is required');
    }
    if (!formData.from_address) {
      errors.push('Supplier/Consignor Address is required');
    }
    if (!formData.to_name) {
      errors.push('Buyer/Consignee Name is required');
    }
    if (!formData.to_address) {
      errors.push('Buyer/Consignee Address is required');
    }
    
    // For generating E-Way Bill (not draft), additional validations
    if (!isDraft) {
      if (!formData.distance_km || parseFloat(formData.distance_km) <= 0) {
        errors.push('Approximate Distance (KM) is required and must be greater than 0');
      }
      if (!formData.vehicle_number) {
        errors.push('Vehicle Number is required');
      }
      if (!formData.vehicle_type) {
        errors.push('Vehicle Type is required');
      }
      if (!formData.vehicle_type_description) {
        errors.push('Vehicle Type Description is required');
      }
    }
    
    return errors;
  };

  // Handle Save as Draft
  const handleSaveDraft = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm(true);
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }

    // Helper to convert empty strings to null for date fields
    const cleanDateField = (value) => {
      return value && value.trim() !== '' ? value : null;
    };

    try {
      const payload = {
        ...formData,
        distance_km: parseFloat(formData.distance_km) || 0,
        valid_upto: validUpto || formData.document_date || new Date().toISOString().split('T')[0],
        ewb_date: formData.document_date || new Date().toISOString().split('T')[0],
        transport_document_date: cleanDateField(formData.transport_document_date)
      };
      
      // If editing (ewayBillId exists), use PUT to update; otherwise use POST
      let response;
      if (ewayBillId && currentEWayBillNumber) {
        // Update existing E-Way Bill using ewb_number (unique identifier)
        payload.ewb_number = currentEWayBillNumber;
        response = await CommonService.put(`/eway-bill/${ewayBillId}`, payload);
      } else {
        // Create new or update if duplicate exists (handled by backend)
        response = await CommonService.post('/eway-bill/draft', payload);
      }

      if (response && response.success) {
        toast.success('E-Way Bill saved as draft successfully');
        navigate(isLogisticsFlow ? '/logistics-rfqs-sent' : '/eway-generation-list');
      } else {
        toast.error(response?.error || 'Failed to save E-Way Bill draft');
      }
    } catch (error) {
      console.error('Error saving E-Way Bill draft:', error);
      toast.error(error.response?.data?.error || 'Failed to save E-Way Bill draft');
    }
  };

  // Handle Generate E-Way Bill
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm(false);
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }

    if (!isLogisticsFlow && !formData.invoice_id) {
      toast.error('Invoice ID is required');
      return;
    }
    if (isLogisticsFlow && !formData.logistics_quote_id) {
      toast.error('Logistics Quote ID is missing');
      return;
    }

    // Helper to convert empty strings to null for date fields
    const cleanDateField = (value) => {
      return value && value.trim() !== '' ? value : null;
    };

    try {
      const payload = {
        ...formData,
        distance_km: parseFloat(formData.distance_km) || 0,
        valid_upto: validUpto || formData.document_date,
        ewb_date: formData.document_date || new Date().toISOString().split('T')[0],
        transport_document_date: cleanDateField(formData.transport_document_date),
        status: 'Active' // Set status to Active when generating E-Way Bill
      };
      
      // If editing (ewayBillId exists), use PUT to update; otherwise use POST
      let response;
      if (ewayBillId && currentEWayBillNumber) {
        // Update existing E-Way Bill using ewb_number (unique identifier)
        payload.ewb_number = currentEWayBillNumber;
        response = await CommonService.put(`/eway-bill/${ewayBillId}`, payload);
      } else {
        // Create new or update if duplicate exists (handled by backend)
        response = await CommonService.post('/eway-bill', payload);
      }

      if (response && response.success) {
        toast.success('E-Way Bill generated successfully');
        navigate(isLogisticsFlow ? '/logistics-rfqs-sent' : '/eway-generation-list');
      } else {
        toast.error(response?.error || 'Failed to generate E-Way Bill');
      }
    } catch (error) {
      console.error('Error generating E-Way Bill:', error);
      toast.error(error.response?.data?.error || 'Failed to generate E-Way Bill');
    }
  };

  return (
    <div className="eway-bill-generate">
      <div className="ewbg-main-content">
        <div className="ewbg-header">
          <div className="ewbg-header-left">
            <button
              className="ewbg-back-btn"
              onClick={() => navigate(isLogisticsFlow ? '/logistics-rfqs-sent' : '/eway-generation-list')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              {isLogisticsFlow ? 'Back to Logistics RFQs' : 'Back to Invoices'}
            </button>
            <h1 className="ewbg-main-title">Generate E-way Bill</h1>
            <p className="ewbg-subtitle">Electronic waybill for GST compliance during goods transportation</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Select Invoice (hidden for logistics flow) */}
          {!isLogisticsFlow && (
            <div className="ewbg-section">
              <div className="ewbg-section-header ewbg-section-blue">
                <h3 className="ewbg-section-title">Select Invoice</h3>
              </div>
              <div className="ewbg-section-content">
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Select Invoice / Document <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.invoice_id || ''}
                    onChange={(e) => handleInputChange('invoice_id', e.target.value ? parseInt(e.target.value) : null)}
                    required
                  >
                    <option value="">Choose an invoice...</option>
                    {invoiceId && <option value={invoiceId}>Invoice #{invoiceId}</option>}
                  </select>
                </div>
                
                {/* Invoice Details Card */}
                {invoiceData && (
                  <div className="ewbg-invoice-card">
                    <div className="ewbg-invoice-card-row">
                      <div className="ewbg-invoice-card-item">
                        <label className="ewbg-invoice-card-label">Invoice Number</label>
                        <p className="ewbg-invoice-card-value">{invoiceData.invoice_number || 'N/A'}</p>
                      </div>
                      <div className="ewbg-invoice-card-item">
                        <label className="ewbg-invoice-card-label">Invoice Date</label>
                        <p className="ewbg-invoice-card-value">{formatDate(invoiceData.invoice_date)}</p>
                      </div>
                      <div className="ewbg-invoice-card-item">
                        <label className="ewbg-invoice-card-label">Invoice Value</label>
                        <p className="ewbg-invoice-card-value">{formatCurrency(calculateTotalInvoiceValue())}</p>
                      </div>
                      <div className="ewbg-invoice-card-item">
                        <label className="ewbg-invoice-card-label">Buyer Name</label>
                        <p className="ewbg-invoice-card-value">{invoiceData.buyer_company_name || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Document Details */}
          <div className="ewbg-section">
            <div className="ewbg-section-header ewbg-section-blue">
              <h3 className="ewbg-section-title">Document Details</h3>
            </div>
            <div className="ewbg-section-content">
              <div className="ewbg-form-row">
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Document Type <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.document_type}
                    onChange={(e) => handleInputChange('document_type', e.target.value)}
                    required
                  >
                    <option value="Tax Invoice">Tax Invoice</option>
                    <option value="Bill of Supply">Bill of Supply</option>
                    <option value="Delivery Challan">Delivery Challan</option>
                  </select>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Document Number <span className="ewbg-required">*</span></label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.document_number}
                    readOnly
                    required
                  />
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Document Date <span className="ewbg-required">*</span></label>
                  <input
                    type="date"
                    className="ewbg-input"
                    value={formData.document_date}
                    readOnly
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Seller and Buyer Information - Side by Side */}
          <div className="ewbg-parties-container">
            {/* Supplier / Consignor (From) */}
            <div className="ewbg-section">
              <div className="ewbg-section-header ewbg-section-orange">
                <h3 className="ewbg-section-title">
                  {isLogisticsFlow ? 'Consignor (From)' : 'Supplier / Consignor (From)'}
                </h3>
              </div>
              <div className="ewbg-section-content">
                <div className="ewbg-form-row">
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">Name <span className="ewbg-required">*</span></label>
                    <input
                      type="text"
                      className="ewbg-input"
                      value={formData.from_name}
                      onChange={(e) => handleInputChange('from_name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">GSTIN <span className="ewbg-required">*</span></label>
                    <input
                      type="text"
                      className="ewbg-input"
                      value={formData.from_gstin}
                      onChange={(e) => handleInputChange('from_gstin', e.target.value)}
                      required
                      maxLength={15}
                    />
                  </div>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Address <span className="ewbg-required">*</span></label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.from_address}
                    onChange={(e) => handleInputChange('from_address', e.target.value)}
                    required
                  />
                </div>
                <div className="ewbg-form-row">
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">Country <span className="ewbg-required">*</span></label>
                    <select
                      className="ewbg-select"
                      value={formData.from_country}
                      onChange={(e) => {
                        const countryLabel = e.target.value;
                        handleInputChange('from_country', countryLabel);
                        // Find country ID from label
                        const country = countries.find(c => c.label === countryLabel);
                        if (country) {
                          updateListsForCountry(country.id || country.value);
                        }
                      }}
                      required
                    >
                      {countries.map(country => (
                        <option key={country.id || country.value} value={country.label}>{country.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">State <span className="ewbg-required">*</span></label>
                    <select
                      className="ewbg-select"
                      value={formData.from_state}
                      onChange={(e) => {
                        const stateLabel = e.target.value;
                        handleInputChange('from_state', stateLabel);
                        // Clear city when state changes
                        handleInputChange('from_city', '');
                        // Find country and state IDs from labels
                        const country = countries.find(c => c.label === formData.from_country);
                        const state = states.find(s => s.label === stateLabel);
                        if (country && state) {
                          const countryId = country.id || country.value;
                          const stateId = state.id || state.value;
                          const stateKey = `${countryId}-${stateId}`;
                          loadedFromStateRef.current = stateKey;
                          updateListsForState(countryId, stateId);
                        }
                      }}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state.id || state.value} value={state.label}>{state.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="ewbg-form-row">
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">City <span className="ewbg-required">*</span></label>
                    <select
                      className="ewbg-select"
                      value={formData.from_city}
                      onChange={(e) => handleInputChange('from_city', e.target.value)}
                      required
                    >
                      <option value="">Select City</option>
                      {cities && cities.length > 0 ? (
                        cities.map(city => (
                          <option key={city.id || city.value} value={city.label}>{city.label}</option>
                        ))
                      ) : (
                        <option value="">No cities available</option>
                      )}
                    </select>
                  </div>
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">Pincode <span className="ewbg-required">*</span></label>
                    <input
                      type="text"
                      className="ewbg-input"
                      value={formData.from_pincode}
                      onChange={(e) => handleInputChange('from_pincode', e.target.value)}
                      required
                      maxLength={6}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Buyer / Consignee (To) */}
            <div className="ewbg-section">
              <div className="ewbg-section-header ewbg-section-green">
                <h3 className="ewbg-section-title">
                  {isLogisticsFlow ? 'Consignee (To)' : 'Buyer / Consignee (To)'}
                </h3>
              </div>
              <div className="ewbg-section-content">
                <div className="ewbg-form-row">
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">Name <span className="ewbg-required">*</span></label>
                    <input
                      type="text"
                      className="ewbg-input"
                      value={formData.to_name}
                      readOnly={!isLogisticsFlow}
                      placeholder="Buyer name"
                      required
                    />
                  </div>
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">GSTIN <span className="ewbg-required">*</span></label>
                    <input
                      type="text"
                      className="ewbg-input"
                      value={formData.to_gstin || ''}
                      readOnly={!isLogisticsFlow}
                      placeholder="15 digit GSTIN"
                      maxLength={15}
                    />
                  </div>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Address <span className="ewbg-required">*</span></label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.to_address}
                    readOnly={!isLogisticsFlow}
                    placeholder="Street address"
                    required
                  />
                </div>
                <div className="ewbg-form-row">
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">Country <span className="ewbg-required">*</span></label>
                    <select
                      className="ewbg-select"
                      value={formData.to_country}
                      required
                      disabled={!isLogisticsFlow}
                      onChange={(e) => {
                        if (!isLogisticsFlow) return;
                        const countryLabel = e.target.value;
                        handleInputChange('to_country', countryLabel);
                        const country = countries.find(c => c.label === countryLabel);
                        if (country) {
                          updateListsForCountry(country.id || country.value);
                        }
                      }}
                    >
                      <option value="">Country</option>
                      {countries.map(country => (
                        <option key={country.id || country.value} value={country.label}>{country.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">State <span className="ewbg-required">*</span></label>
                    <select
                      className="ewbg-select"
                      value={formData.to_state}
                      required
                      disabled={!isLogisticsFlow}
                      onChange={(e) => {
                        if (!isLogisticsFlow) return;
                        const stateLabel = e.target.value;
                        handleInputChange('to_state', stateLabel);
                        handleInputChange('to_city', '');
                        const country = countries.find(c => c.label === formData.to_country);
                        const state = states.find(s => s.label === stateLabel);
                        if (country && state) {
                          updateListsForState(country.id || country.value, state.id || state.value);
                        }
                      }}
                    >
                      <option value="">State</option>
                      {states.map(state => (
                        <option key={state.id || state.value} value={state.label}>{state.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="ewbg-form-row">
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">City <span className="ewbg-required">*</span></label>
                    <select
                      className="ewbg-select"
                      value={formData.to_city}
                      required
                      disabled={!isLogisticsFlow}
                      onChange={(e) => {
                        if (!isLogisticsFlow) return;
                        handleInputChange('to_city', e.target.value);
                      }}
                    >
                      <option value="">Select City</option>
                      {cities && cities.length > 0 ? (
                        cities.map(city => (
                          <option key={city.id || city.value} value={city.label}>{city.label}</option>
                        ))
                      ) : (
                        <option value="">No cities available</option>
                      )}
                    </select>
                  </div>
                  <div className="ewbg-form-group">
                    <label className="ewbg-label">Pincode <span className="ewbg-required">*</span></label>
                    <input
                      type="text"
                      className="ewbg-input"
                      value={formData.to_pincode}
                      readOnly={!isLogisticsFlow}
                      placeholder="Pincode"
                      required
                      maxLength={6}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Type */}
          <div className="ewbg-section">
            <div className="ewbg-section-header ewbg-section-purple">
              <h3 className="ewbg-section-title">Transaction Type</h3>
            </div>
            <div className="ewbg-section-content">
              <div className="ewbg-form-row">
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Supply Type <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.supply_type}
                    onChange={(e) => handleInputChange('supply_type', e.target.value)}
                    required
                  >
                    <option value="Outward (Supply)">Outward (Supply)</option>
                    <option value="Inward (Receipt)">Inward (Receipt)</option>
                  </select>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Sub Supply Type <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.sub_supply_type}
                    onChange={(e) => handleInputChange('sub_supply_type', e.target.value)}
                    required
                  >
                    <option value="Supply">Supply</option>
                    <option value="Import">Import</option>
                    <option value="Export">Export</option>
                  </select>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Transaction Type <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.transaction_type}
                    onChange={(e) => handleInputChange('transaction_type', e.target.value)}
                    required
                  >
                    <option value="Regular">Regular</option>
                    <option value="Bill to Ship to">Bill to Ship to</option>
                    <option value="Bill from Dispatch from">Bill from Dispatch from</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Goods Details (From Invoice) */}
          {invoiceLineItems.length > 0 && (
            <div className="ewbg-section">
              <div className="ewbg-section-header ewbg-section-orange">
                <h3 className="ewbg-section-title">Goods Details (From Invoice)</h3>
              </div>
              <div className="ewbg-section-content">
                <div className="ewbg-table-wrapper">
                  <table className="ewbg-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>HSN Code</th>
                        <th className="ewbg-text-right">Quantity</th>
                        <th className="ewbg-text-right">Taxable Value</th>
                        <th className="ewbg-text-right">Tax Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceLineItems.map((item, index) => {
                        const quantity = parseFloat(item.quantity) || 0;
                        const unitPrice = parseFloat(item.unit_price) || 0;
                        const discount = parseFloat(item.discount) || 0;
                        const itemSubtotal = quantity * unitPrice;
                        const discountAmount = itemSubtotal * (discount / 100);
                        const taxableAmount = itemSubtotal - discountAmount;
                        
                        // Calculate tax amounts
                        const cgstPercent = parseFloat(item.cgst) || 0;
                        const sgstPercent = parseFloat(item.sgst) || 0;
                        const igstPercent = parseFloat(item.igst) || 0;
                        const cessPercent = parseFloat(item.cess) || 0;
                        const customsDutyPercent = parseFloat(item.customs_duty) || 0;
                        const taxPercent = parseFloat(item.tax) || 0;
                        
                        let taxAmount = 0;
                        if (cgstPercent > 0 || sgstPercent > 0) {
                          taxAmount = (taxableAmount * (cgstPercent / 100)) + (taxableAmount * (sgstPercent / 100)) + (taxableAmount * (cessPercent / 100));
                        } else if (customsDutyPercent > 0) {
                          const amountAfterCustoms = taxableAmount + (taxableAmount * (customsDutyPercent / 100));
                          taxAmount = (amountAfterCustoms * (igstPercent / 100)) + (amountAfterCustoms * (cessPercent / 100)) + (taxableAmount * (customsDutyPercent / 100));
                        } else if (taxPercent > 0) {
                          taxAmount = taxableAmount * (taxPercent / 100);
                        } else {
                          taxAmount = (taxableAmount * (igstPercent / 100)) + (taxableAmount * (cessPercent / 100));
                        }
                        
                        const totalAmount = parseFloat(item.total_amount) || (taxableAmount + taxAmount);
                        
                        const unitLabel = getUnitLabel(item.unit) || 'pcs';
                        
                        return (
                          <tr key={index}>
                            <td>{item.description || 'N/A'}</td>
                            <td>{item.hsn_sac || 'N/A'}</td>
                            <td className="ewbg-text-right">{quantity} {unitLabel}</td>
                            <td className="ewbg-text-right">{formatCurrency(taxableAmount)}</td>
                            <td className="ewbg-text-right">{formatCurrency(taxAmount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="ewbg-total-value">
                  <span className="ewbg-total-label">Total Invoice Value:</span>
                  <span className="ewbg-total-amount">{formatCurrency(calculateTotalInvoiceValue())}</span>
                </div>
              </div>
            </div>
          )}

          {/* Transportation Details */}
          <div className="ewbg-section">
            <div className="ewbg-section-header ewbg-section-blue">
              <h3 className="ewbg-section-title">Transportation Details</h3>
            </div>
            <div className="ewbg-section-content">
              <div className="ewbg-form-row">
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Transport Mode <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.transport_mode}
                    onChange={(e) => handleInputChange('transport_mode', e.target.value)}
                    required
                  >
                    <option value="Road">Road</option>
                    <option value="Rail">Rail</option>
                    <option value="Air">Air</option>
                    <option value="Ship">Ship</option>
                  </select>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Approximate Distance (KM) <span className="ewbg-required">*</span></label>
                  <input
                    type="number"
                    className="ewbg-input"
                    value={formData.distance_km}
                    onChange={(e) => handleInputChange('distance_km', e.target.value)}
                    placeholder="Enter distance in KM"
                    required
                    min="0"
                  />
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Valid Upto (Auto-calculated)</label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={validUpto || 'dd-mm-yyyy'}
                    readOnly
                  />
                  <small className="ewbg-help-text">Based on 200 KM/day rule</small>
                </div>
              </div>
              <div className="ewbg-form-row">
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Transporter Name</label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.transporter_name}
                    onChange={(e) => handleInputChange('transporter_name', e.target.value)}
                    placeholder="e.g., ABC Logistics"
                  />
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Transporter ID (GSTIN)</label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.transporter_gstin}
                    onChange={(e) => handleInputChange('transporter_gstin', e.target.value)}
                    placeholder="15 digit GSTIN"
                    maxLength={15}
                  />
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Transport Document No.</label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.transport_document_no}
                    onChange={(e) => handleInputChange('transport_document_no', e.target.value)}
                    placeholder="LR/RR/Airway Bill No."
                  />
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Transport Document Date</label>
                  <input
                    type="date"
                    className="ewbg-input"
                    value={formData.transport_document_date}
                    onChange={(e) => handleInputChange('transport_document_date', e.target.value)}
                  />
                </div>
              </div>
              <div className="ewbg-form-row">
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Vehicle Type <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.vehicle_type}
                    onChange={(e) => handleInputChange('vehicle_type', e.target.value)}
                    required
                  >
                    <option value="Regular">Regular</option>
                    <option value="Over Dimensional Cargo">Over Dimensional Cargo</option>
                  </select>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Vehicle Type Description <span className="ewbg-required">*</span></label>
                  <select
                    className="ewbg-select"
                    value={formData.vehicle_type_description}
                    onChange={(e) => handleInputChange('vehicle_type_description', e.target.value)}
                    required
                  >
                    <option value="">Select vehicle type...</option>
                    <option value="Regular">Regular</option>
                    <option value="Over Dimensional Cargo">Over Dimensional Cargo</option>
                  </select>
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Vehicle Number <span className="ewbg-required">*</span></label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.vehicle_number}
                    onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                    placeholder="e.g., KA01AB1234"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="ewbg-section">
            <div className="ewbg-section-header ewbg-section-blue">
              <h3 className="ewbg-section-title">Additional Information</h3>
            </div>
            <div className="ewbg-section-content">
              <div className="ewbg-form-row">
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Reason for Transportation</label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.reason_for_transportation}
                    onChange={(e) => handleInputChange('reason_for_transportation', e.target.value)}
                  />
                </div>
                <div className="ewbg-form-group">
                  <label className="ewbg-label">Main HSN Code</label>
                  <input
                    type="text"
                    className="ewbg-input"
                    value={formData.main_hsn_code}
                    readOnly
                  />
                </div>
              </div>
              <div className="ewbg-form-group">
                <label className="ewbg-label">Remarks / Special Instructions</label>
                <textarea
                  className="ewbg-textarea"
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  placeholder="Any special handling instructions or remarks..."
                  rows="3"
                />
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="ewbg-footer">
            <button type="button" className="ewbg-btn ewbg-btn-secondary" onClick={handleSaveDraft}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              Save Draft
            </button>
            <button type="submit" className="ewbg-btn ewbg-btn-primary">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                <path d="m9 11 3 3L22 4"></path>
              </svg>
              Generate E-way Bill
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

